import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useForm, Controller } from 'react-hook-form';
import { BUTTON_BORDER_RADIUS, TEXTFIELD_BORDER_RADIUS, SMALL_BORDER_RADIUS } from 'constants/borderRadius';

/**
 * @typedef {object} MFAEnrollmentProps
 * @property {string} phoneNumber - User's phone number (may be masked)
 * @property {Function} onSendCode - Callback to send verification code
 * @property {Function} onVerify - Callback to verify code and complete enrollment
 * @property {boolean} [isLoading=false] - Shows loading state
 * @property {boolean} [codeSent=false] - Whether code has been sent
 * @property {boolean} [isComplete=false] - Whether enrollment is complete
 * @property {string} [errorMessage] - Error to display
 * @property {Function} [onBack] - Optional callback to go back to email step
 * @property {Function} [onClearError] - Callback to clear external error when user types
 */

/**
 * MFA enrollment flow component.
 * Guides users through setting up multi-factor authentication.
 *
 * @param {MFAEnrollmentProps} props - Component props
 * @returns {React.ReactElement}
 */
const MFAEnrollment = ({
    phoneNumber,
    onSendCode,
    onVerify,
    isLoading = false,
    codeSent = false,
    isComplete = false,
    errorMessage,
    onBack,
    onClearError,
}) => {
    const [step, setStep] = useState(codeSent ? 'verify' : 'send');

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            code: '',
        },
    });

    /**
     * Handles sending the verification code
     */
    const handleSendCode = async () => {
        try {
            const success = await onSendCode();
            // Only transition to verify step if send was successful
            if (success !== false) {
                setStep('verify');
            }
        } catch (err) {
            // Error handled by parent, don't transition
            console.error('Send code failed:', err);
        }
    };

    /**
     * Handles form submission for verification
     * @param {{code: string}} data - Form data
     */
    const onFormSubmit = (data) => {
        onVerify(data.code.trim());
    };

    // Success state
    if (isComplete) {
        return (
            <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircleIcon
                    color="success"
                    sx={{ fontSize: 64, mb: 2 }}
                />
                <Typography variant="h6" gutterBottom>
                    MFA Enabled Successfully!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Your account is now protected with multi-factor authentication.
                    You will be redirected shortly.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Multi-Factor Authentication Required
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Your organization requires additional account security.
                    Please set up multi-factor authentication to continue.
                </Typography>
            </Alert>

            {/* Phone number display */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    mb: 2,
                    borderRadius: SMALL_BORDER_RADIUS,
                    backgroundColor: 'action.hover',
                }}
            >
                <PhoneAndroidIcon color="action" />
                <Typography variant="body1">
                    {phoneNumber}
                </Typography>
            </Box>

            {/* Send Code Step */}
            <Collapse in={step === 'send'}>
                <Button
                    id="mfa-enroll-send-btn"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={handleSendCode}
                    disabled={isLoading}
                    startIcon={
                        isLoading
                            ? <CircularProgress size={20} color="inherit" />
                            : <SendIcon />
                    }
                    sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
                >
                    {isLoading ? 'Sending...' : 'Send Code'}
                </Button>
            </Collapse>

            {/* Verify Code Step */}
            <Collapse in={step === 'verify'}>
                <Box
                    component="form"
                    onSubmit={handleSubmit(onFormSubmit)}
                    noValidate
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter the verification code sent to your phone.
                    </Typography>

                    <Controller
                        name="code"
                        control={control}
                        rules={{
                            required: 'Verification code is required',
                            pattern: {
                                value: /^\d{6}$/,
                                message: 'Please enter a 6-digit code',
                            },
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                onChange={(e) => {
                                    field.onChange(e);
                                    // Clear external error when user starts typing
                                    if (errorMessage && onClearError) {
                                        onClearError();
                                    }
                                }}
                                id="mfa-enroll-code-input"
                                label="Verification Code"
                                placeholder="123456"
                                fullWidth
                                autoComplete="one-time-code"
                                autoFocus
                                disabled={isLoading}
                                error={!!errors.code || !!errorMessage}
                                helperText={errors.code?.message || errorMessage || ' '}
                                slotProps={{
                                    htmlInput: {
                                        maxLength: 6,
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                        style: {
                                            textAlign: 'center',
                                            letterSpacing: '0.5em',
                                            fontSize: '1.5rem',
                                            fontWeight: 600,
                                        },
                                    },
                                }}
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: TEXTFIELD_BORDER_RADIUS,
                                    },
                                }}
                            />
                        )}
                    />

                    <Button
                        id="mfa-enroll-verify-btn"
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        disabled={isLoading}
                        startIcon={
                            isLoading
                                ? <CircularProgress size={20} color="inherit" />
                                : <CheckCircleIcon />
                        }
                        sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
                    >
                        {isLoading ? 'Enabling...' : 'Enable MFA'}
                    </Button>
                </Box>
            </Collapse>

            {/* Back to email link */}
            {onBack && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={onBack}
                        disabled={isLoading}
                        sx={{ cursor: 'pointer' }}
                    >
                        Start over
                    </Link>
                </Box>
            )}
        </Box>
    );
};

MFAEnrollment.propTypes = {
    phoneNumber: PropTypes.string.isRequired,
    onSendCode: PropTypes.func.isRequired,
    onVerify: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    codeSent: PropTypes.bool,
    isComplete: PropTypes.bool,
    errorMessage: PropTypes.string,
    onBack: PropTypes.func,
    onClearError: PropTypes.func,
};

export default MFAEnrollment;
